import { useState } from "react";
import { Loader2, MapPin, Send } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

const INITIAL = { name: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.message.trim()) errs.message = "Required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Check the highlighted fields");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      setForm(INITIAL);
      toast.success("Message sent", {
        description: "It lands directly in our inbox — we'll get back to you soon.",
      });
    } catch {
      toast.error("Couldn't send right now", {
        description: "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  const fieldCls = (key) =>
    `w-full rounded-[3px] border bg-white px-4 py-3 text-sm text-ink placeholder:text-zinc-400 transition-colors duration-200 focus:border-volt focus:outline-none ${
      errors[key] ? "border-red-500" : "border-black/20"
    }`;

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <section className="py-16 sm:py-20">
        <p className="eyebrow mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          Contact
        </p>
        <h1
          className="font-display font-black tracking-tight text-ink leading-[0.95]"
          style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}
          data-testid="contact-heading"
        >
          <MaskedLines
            lines={[
              <span key="a">LET'S TALK</span>,
              <span key="b">
                <span className="text-volt">ELECTRIC.</span>
              </span>,
            ]}
          />
        </h1>
        <Reveal delay={0.35}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
            Fit checks, installation, pickup or shipping questions — every
            message lands directly in our inbox in Auckland.
          </p>
        </Reveal>
      </section>

      <div className="grid gap-10 lg:grid-cols-3">
        <Reveal className="lg:col-span-2">
          {sent ? (
            <div className="flex h-full min-h-[320px] flex-col items-start justify-center gap-4 rounded-2xl border border-volt/30 bg-white p-10" data-testid="contact-success">
              <Send className="h-8 w-8 text-volt" />
              <h2 className="font-display text-2xl font-extrabold text-ink">Message sent.</h2>
              <p className="max-w-md text-sm leading-relaxed text-zinc-600">
                Thanks — it's in our inbox. We'll get back to you soon. Need
                something else? Send another message.
              </p>
              <button
                type="button"
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
                className="rounded-[3px] border border-black/25 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:border-volt hover:text-volt"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-5 rounded-2xl border border-black/10 bg-white p-8" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Name <span className="text-volt">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-name-input"
                    className={fieldCls("name")}
                    placeholder="Your name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Email <span className="text-volt">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-email-input"
                    className={fieldCls("email")}
                    placeholder="you@example.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  Message <span className="text-volt">*</span>
                </label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message-input"
                  className={fieldCls("message")}
                  placeholder="Bike model, what you're after, questions…"
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-[3px] bg-volt px-8 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.1} className="space-y-4">
          <div className="rounded-2xl border border-black/10 bg-surface p-6" data-testid="contact-info">
            <MapPin className="h-6 w-6 text-volt" />
            <h3 className="mt-4 font-display text-lg font-extrabold text-ink">Auckland, New Zealand</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              We're a small operation, so email through this form is the
              fastest way to reach us — usually a reply within a day.
            </p>
          </div>
          <div className="rounded-2xl border border-black/10 bg-surface p-6">
            <h3 className="font-display text-lg font-extrabold text-ink">Fit checks welcome</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              Send your bike's make, model and year — we'll confirm whether a
              hub motor kit fits before you pay anything.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
